import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, ExternalLink, Download } from 'lucide-react';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminTaskReport() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const { session } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ submissions: [], task_info: {} });
  const [clickStats, setClickStats] = useState({
    total_clicks: 0,
    unique_clicks: 0,
    unique_users: 0,
    clicks_by_user: [],
    token_count: 0,
    event_count: 0,
    tracking_base_url: '',
  });

  const fetchData = useCallback(async () => {
    if (!session?.access_token) return;
    
    setLoading(true);
    try {
      // Fetch task report data
      const response = await fetch(`${BACKEND_URL}/api/admin/tasks/${taskId}/export`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch data');
      
      const result = await response.json();
      setData(result);
      
      // Fetch click stats
      try {
        console.log('Fetching click stats for task:', taskId);
        const clickResponse = await fetch(`${BACKEND_URL}/api/admin/click-stats/task/${taskId}`, {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        });
        console.log('Click stats response status:', clickResponse.status);
        if (clickResponse.ok) {
          const clickData = await clickResponse.json();
          console.log('Click stats data:', clickData);
          setClickStats(clickData);
        } else {
          console.log('Click stats failed:', await clickResponse.text());
        }
      } catch (clickError) {
        console.log('Click stats error:', clickError);
      }
    } catch (error) {
      console.error('Error fetching task report:', error);
      toast.error('Rapor yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [session?.access_token, taskId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data.submissions);
    XLSX.utils.book_append_sheet(wb, ws, 'Katılımcılar');
    XLSX.writeFile(wb, `${data.task_info.gorev_adi}_rapor.xlsx`);
    toast.success('Excel indirildi');
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'İnceleniyor': <Badge className="bg-brand-yellow text-brand-ink">İnceleniyor</Badge>,
      'Tamamlandı': <Badge className="bg-green-100 text-green-800">Tamamlandı</Badge>,
      'Reddedildi': <Badge className="bg-red-100 text-red-800">Reddedildi</Badge>,
    };
    return statusMap[status] || <Badge>{status}</Badge>;
  };

  return (
    <div data-testid="admin-task-report">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/tasks')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Geri
          </Button>
          <div>
            <h2 className="font-brand text-2xl text-brand-ink">Görev Raporu</h2>
            {!loading && (
              <p className="text-sm text-gray-500 mt-1">
                {data.task_info.gorev_adi} • {data.task_info.kampanya_adi} • {data.task_info.platform}
              </p>
            )}
          </div>
        </div>
        <Button onClick={exportToExcel} disabled={loading || data.submissions.length === 0}>
          <Download className="w-4 h-4 mr-2" />
          Excel İndir
        </Button>
      </div>

      {/* Stats */}
      {!loading && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Toplam Katılımcı</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{new Set(data.submissions.map(s => s.katilimci_no)).size}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Toplam Gönderim</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{data.submissions.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Geçerli Click</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{clickStats.unique_clicks ?? clickStats.total_clicks}</p>
              <p className="mt-1 text-xs text-gray-500">Ham: {clickStats.total_clicks}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Kontenjan</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{data.task_info.kontenjan || '-'}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {!loading && clickStats.token_count > 0 && clickStats.event_count === 0 && (
        <div className="mb-6 rounded-lg border border-brand-red bg-brand-red-soft p-4 text-sm text-brand-red-deep">
          Bu görev için {clickStats.token_count} tracking token üretilmiş ama henüz kayıtlı tıklama yok.
          Tracking domain: {clickStats.tracking_base_url || '-'}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : data.submissions.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-600">Henüz katılımcı bulunmuyor</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">Katılımcı No</TableHead>
                <TableHead>E-posta</TableHead>
                <TableHead>Ad Soyad</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead className="text-center">Gönderim Sayısı</TableHead>
                <TableHead className="text-center">Geçerli Click</TableHead>
                <TableHead>Kazanç</TableHead>
                <TableHead>Link</TableHead>
                <TableHead>Tracking</TableHead>
                <TableHead>Görsel</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.submissions.map((submission, idx) => {
                // Find click count for this user by email or user_id
                const userClicks = clickStats.clicks_by_user.find(
                  c => c.email === submission.kullanici_email || c.user_id === submission.user_id
                );
                return (
                <TableRow key={idx}>
                  <TableCell className="font-mono text-sm">{submission.katilimci_no || '-'}</TableCell>
                  <TableCell className="text-sm">{submission.kullanici_email}</TableCell>
                  <TableCell>{submission.kullanici_adi}</TableCell>
                  <TableCell>{getStatusBadge(submission.gorev_durumu)}</TableCell>
                  <TableCell className="text-center">{submission.gonderim_sayisi}</TableCell>
                  <TableCell className="text-center">
                    <div>{userClicks?.click_count || 0}</div>
                    {(userClicks?.raw_click_count || 0) > (userClicks?.click_count || 0) && (
                      <div className="text-xs text-gray-500">Ham: {userClicks.raw_click_count}</div>
                    )}
                  </TableCell>
                  <TableCell>{submission.kazanilan_ucret}</TableCell>
                  <TableCell>
                    {submission.yuklenen_link !== '-' && (
                      <a
                        href={submission.yuklenen_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-ink font-semibold underline-offset-2 hover:underline flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Goruntule
                      </a>
                    )}
                  </TableCell>
                  <TableCell>
                    {submission.tracking_link && submission.tracking_link !== '-' && (
                      <a
                        href={submission.tracking_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-ink font-semibold hover:underline text-xs font-mono truncate max-w-[120px] block"
                        title={submission.tracking_link}
                      >
                        {submission.tracking_link.split('/r/')[1] || submission.tracking_link}
                      </a>
                    )}
                  </TableCell>
                  <TableCell>
                    {submission.submitted_image_url && (
                      <a href={submission.submitted_image_url} target="_blank" rel="noopener noreferrer"
                        className="text-brand-ink font-semibold hover:underline text-xs flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" />
                        Görsel
                      </a>
                    )}
                  </TableCell>
                </TableRow>
              );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
